import * as React from "react"
import type { Room } from "../../../recoil/atoms/room/types"
import { List, Box } from "@chakra-ui/react"
import { useRecoilValue, useRecoilState } from "recoil"
import styled from "@emotion/styled"
import { brandingSelector } from "../../../recoil/atoms/room"
import { playerPropsSelector } from "../../../recoil/atoms/player"
import { useWatchParty } from "../../../hooks/useWatchParty"
import { watchPartyHideControlsSelector } from "../../../recoil/atoms/watchPartyScreen"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import useWindowSize from "../../../hooks/useWindowSize"
import {
  taggedBadgesListSelector,
  taggedBadgesFilterState
} from "../../../recoil/atoms/taggedBadges"
import HistoryBadge from "../badges/_historyBadge"
import { FadeBadges } from "../../servicesPanel/badges/_fadeBadges"
import SearchBar from "../badges/components/SearchBar"
import { useTranslation } from "react-i18next"

interface TaggedProps {
  isMobile?: boolean
  isTransparent?: boolean
  height?: number
}

interface StyledProps {
  room?: Room
}
const Container = styled(List)<StyledProps>`
  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`

export const Tagged: React.FC<TaggedProps> = React.memo(
  ({ isMobile, isTransparent, height }) => {
    const { t } = useTranslation()
    const roomStyles = useRecoilValue(brandingSelector)
    const { isWatchParty, watchPartyHostId } = useWatchParty()
    const hideControls = useRecoilValue(watchPartyHideControlsSelector)
    const { isOpenControlsMobile } = useRecoilValue(playerPropsSelector)
    const { isLandscape } = useRecoilValue(viewDependingLayoutAtom)
    const { screenSize } = useWindowSize()
    const taggedBadgesList = useRecoilValue(taggedBadgesListSelector)
    const [value, setValue] = useRecoilState(taggedBadgesFilterState)

    const customStyles = React.useMemo(() => {
      return {
        button: {
          background:
            roomStyles?.secondaryAccessButtonBackgroundColor ?? "#1AC541",
          borderColor:
            roomStyles?.secondaryAccessButtonBorderColor ?? "transparent",
          border: roomStyles?.secondaryAccessButtonBorderColor
            ? `1px solid ${
                roomStyles?.secondaryAccessButtonBorderColor
                  ? roomStyles.secondaryAccessButtonBorderColor
                  : "transparent"
              }`
            : "0px",
          color: roomStyles?.secondaryAccessButtonTextColor ?? "#FFFFFF",
          _hoverStyles: {
            background: `${
              roomStyles?.secondaryAccessButtonMouseoverColor ?? "#24A840"
            } !important`,
            color: `${
              roomStyles?.secondaryAccessButtonTextMouseoverColor ?? "#FFFFFF"
            } !important`
          }
        },
        container: {
          _hoverStyles: {
            opacity: 1
          }
        }
      }
    }, [
      roomStyles?.secondaryAccessButtonBackgroundColor,
      roomStyles.secondaryAccessButtonBorderColor,
      roomStyles?.secondaryAccessButtonMouseoverColor,
      roomStyles?.secondaryAccessButtonTextColor,
      roomStyles?.secondaryAccessButtonTextMouseoverColor
    ])

    return (
      <>
        {!isMobile && <SearchBar value={value} setValue={setValue} />}
        <Box
          p="1rem 0rem"
          mr={!isMobile && "0.5rem"}
          height="100%"
          overflowX="hidden"
          overflowY="hidden"
        >
          <Container
            zIndex={isMobile ? "10000" : "unset"}
            position={isTransparent ? "absolute" : "relative"}
            left={isTransparent && "10px"}
            bottom={
              isWatchParty &&
              isTransparent &&
              isOpenControlsMobile &&
              !hideControls &&
              watchPartyHostId !== "modNotPresentImage" &&
              watchPartyHostId !== "modNotPresentOverlay"
                ? "65px"
                : isLandscape
                ? "20px"
                : !isTransparent
                ? "0px"
                : "12px"
            }
            roomStyles={roomStyles}
            overflowX={!isTransparent ? "auto" : "unset"}
            width={
              isLandscape ? 400 : isTransparent ? screenSize.width - 60 : "100%"
            }
            p={
              isTransparent
                ? "0rem"
                : isMobile
                ? "0.625rem"
                : "0rem 0.5rem 0 1rem"
            }
            overflowY={!isTransparent ? "auto" : "unset"}
            scrollbarwidth="thin"
            style={{ scrollBehavior: "smooth" }}
            spacing={isMobile ? 0 : 4}
            h={isTransparent ? "unset" : isMobile ? height ?? "100%" : "100%"}
          >
            {taggedBadgesList.length === 0 && (
              <FadeBadges
                isSmallView={isMobile}
                infoText={t(
                  "When a shoppable video is playing, any products that have been tagged to that video via the Dashboard will appear here. Audience members have a single <i>Shop</i> tab which will either display Featured products, or Tagged products if they are watching a shoppable video which has had products tagged to it."
                )}
              />
            )}
            {taggedBadgesList?.map(singleBadgeData => (
              <HistoryBadge
                badgeHistoryData={singleBadgeData}
                key={singleBadgeData.id}
                isModerator={false}
                customStyles={customStyles}
              />
            ))}
          </Container>
        </Box>
      </>
    )
  }
)

export default Tagged
